import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { useAuth } from '../../hooks/useAuth';
import { useEmailVerificationGate } from '../../hooks/useEmailVerificationGate';
import { Button } from '../../components/ui/Button';
import type { TastingNote } from '@wineryxplorer/shared';

interface WineTastingNotesProps {
  wineId: string;
  wineName: string;
}

const SCORE_LABELS: { min: number; max: number; label: string }[] = [
  { min: 50, max: 60, label: 'Not for me' },
  { min: 61, max: 70, label: 'Below average' },
  { min: 71, max: 80, label: 'Good' },
  { min: 81, max: 90, label: 'Very good' },
  { min: 91, max: 100, label: 'Outstanding' },
];

const getScoreLabel = (score: number) => {
  const match = SCORE_LABELS.find((l) => score >= l.min && score <= l.max);
  return match?.label ?? '';
};

const getScoreColor = (score: number) => {
  if (score >= 91) return 'text-green-700 bg-green-50';
  if (score >= 81) return 'text-primary-700 bg-primary-50';
  if (score >= 71) return 'text-yellow-700 bg-yellow-50';
  if (score >= 61) return 'text-orange-700 bg-orange-50';
  return 'text-red-700 bg-red-50';
};

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface TastingNoteFormProps {
  initialValues?: { dateTasted: string; score: number; notes: string };
  onSubmit: (data: { dateTasted: string; score: number; notes: string }) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  submitLabel: string;
}

const TastingNoteForm = ({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel,
}: TastingNoteFormProps) => {
  const today = new Date().toISOString().split('T')[0];
  const [dateTasted, setDateTasted] = useState(initialValues?.dateTasted ?? today);
  const [score, setScore] = useState(initialValues?.score ?? 75);
  const [notes, setNotes] = useState(initialValues?.notes ?? '');

  const handleSubmit = () => {
    if (!notes.trim()) return;
    onSubmit({ dateTasted, score, notes: notes.trim() });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitting || !notes.trim()}
          loading={isSubmitting}
        >
          {submitLabel}
        </Button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Date Tasted</label>
        <input
          type="date"
          value={dateTasted}
          max={today}
          onChange={(e) => setDateTasted(e.target.value)}
          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-info-500 focus:ring-1 focus:ring-info-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Score: {score} — {getScoreLabel(score)}
        </label>
        <input
          type="range"
          min={50}
          max={100}
          value={score}
          onChange={(e) => setScore(Number(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          {SCORE_LABELS.map((l) => (
            <span key={l.label}>{l.label}</span>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Describe your tasting experience..."
          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-info-500 focus:ring-1 focus:ring-info-500"
        />
      </div>
    </div>
  );
};

export const WineTastingNotes = ({ wineId, wineName }: WineTastingNotesProps) => {
  const { user } = useAuth();
  const { requireVerified, gateModal } = useEmailVerificationGate();
  const utils = trpc.useUtils();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, isPending } = trpc.tastingNote.list.useQuery(
    { wineId, limit: 50 },
    { enabled: !!user }
  );

  const createMutation = trpc.tastingNote.create.useMutation({
    onSuccess: () => {
      setShowForm(false);
      utils.tastingNote.list.invalidate({ wineId });
    },
  });

  const updateMutation = trpc.tastingNote.update.useMutation({
    onSuccess: () => {
      setEditingId(null);
      utils.tastingNote.list.invalidate({ wineId });
    },
  });

  const deleteMutation = trpc.tastingNote.delete.useMutation({
    onSuccess: () => {
      setDeletingId(null);
      utils.tastingNote.list.invalidate({ wineId });
    },
  });

  if (!user) return null;

  const notes = (data?.items ?? []) as TastingNote[];

  const handleAddTastingNoteClick = () => {
    if (!requireVerified()) return;
    setShowForm(true);
  };

  return (
    <div className="border-t border-gray-200 pt-6">
      {gateModal}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">My Tasting Notes</h2>
        {!showForm && !editingId && (
          <Button variant="outline" onClick={handleAddTastingNoteClick}>
            Add Tasting Note
          </Button>
        )}
      </div>

      {showForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            New Tasting Note for {wineName}
          </h3>
          {createMutation.error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {createMutation.error.message}
            </div>
          )}
          <TastingNoteForm
            onSubmit={(data) =>
              createMutation.mutate({ wineId, ...data })
            }
            onCancel={() => setShowForm(false)}
            isSubmitting={createMutation.isPending}
            submitLabel="Save Note"
          />
        </div>
      )}

      {isPending && (
        <p className="text-sm text-gray-500">Loading tasting notes...</p>
      )}

      {!isPending && notes.length === 0 && !showForm && (
        <p className="text-sm text-gray-500">
          You haven't added any tasting notes for this wine yet.
        </p>
      )}

      <div className="space-y-4">
        {notes.map((note) => {
          const noteId = note._id;
          const isEditing = editingId === noteId;

          if (isEditing) {
            return (
              <div key={noteId} className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Tasting Note</h3>
                {updateMutation.error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                    {updateMutation.error.message}
                  </div>
                )}
                <TastingNoteForm
                  initialValues={{
                    dateTasted: new Date(note.dateTasted).toISOString().split('T')[0],
                    score: note.score,
                    notes: note.notes,
                  }}
                  onSubmit={(data) => updateMutation.mutate({ id: noteId, ...data })}
                  onCancel={() => setEditingId(null)}
                  isSubmitting={updateMutation.isPending}
                  submitLabel="Update Note"
                />
              </div>
            );
          }

          return (
            <div key={noteId} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getScoreColor(note.score)}`}
                    >
                      {note.score} — {getScoreLabel(note.score)}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatDate(note.dateTasted)}
                    </span>
                  </div>
                  <p className="text-gray-700 text-sm">{note.notes}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => setEditingId(noteId)}
                    className="text-sm text-gray-500 hover:text-primary-600 px-2 py-1"
                  >
                    Edit
                  </button>
                  {deletingId === noteId ? (
                    <div className="flex gap-1">
                      <button
                        onClick={() => deleteMutation.mutate({ id: noteId })}
                        disabled={deleteMutation.isPending}
                        className="text-sm text-red-600 hover:text-red-700 px-2 py-1 font-medium"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setDeletingId(null)}
                        className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeletingId(noteId)}
                      className="text-sm text-gray-500 hover:text-red-600 px-2 py-1"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
